// src/ui/widgets/BottomNavBar.jsx

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserAvatar } from '../../components/UserAvatar';
import { userProfileService } from '../../services/user/userProfileService';

import chatIcon from '../../assets/svg/chat_icon.svg';
import chatFilled from '../../assets/svg/chat_filled.svg';
import homeIcon from '../../assets/svg/home_icon.svg';
import homeFilled from '../../assets/svg/home_filled.svg';

const WHITE = '#FFFFFF';
const GREY = '#7A7A7A';
const BLUE = '#4DA3FF';

// ── SvgIcon ───────────────────────────────────────────────────────────────

function SvgIcon({ src, size, color }) {
  return (
    <span
      style={{
        display: 'inline-block',
        width: size,
        height: size,
        backgroundColor: color,
        WebkitMaskImage: `url(${src})`,
        maskImage: `url(${src})`,
        WebkitMaskSize: 'contain',
        maskSize: 'contain',
        WebkitMaskRepeat: 'no-repeat',
        maskRepeat: 'no-repeat',
        WebkitMaskPosition: 'center',
        maskPosition: 'center',
        pointerEvents: 'none', // never let the icon itself swallow the click
      }}
    />
  );
}

// ── NavIcon wrapper ───────────────────────────────────────────────────────

function NavIcon({ children, isActive, isAvatar = false, isLarge = false }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: 20,
        backgroundColor: isActive ? 'rgba(255,255,255,0.12)' : 'transparent',
        padding: `${isAvatar ? 4 : 6}px ${isLarge ? 14 : 16}px`,
        transition: 'background-color 150ms ease',
        pointerEvents: 'none', // clicks pass through to the parent <button>
      }}
    >
      {children}
    </div>
  );
}

// ── Profile avatar with active outline ────────────────────────────────────

function ProfileAvatar({ isActive, profile }) {
  const avatar = (
    <UserAvatar
      size={26}
      imageUrl={profile?.hasProfileImage ? profile.profileImage : undefined}
      name={profile?.displayName}
    />
  );

  if (!isActive) return avatar;

  return (
    <div
      style={{
        width: 32,
        height: 32,
        borderRadius: '50%',
        border: `1.8px solid ${WHITE}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {avatar}
    </div>
  );
}

// ── Unread badge ──────────────────────────────────────────────────────────

function UnreadBadge({ count }) {
  if (count <= 0) return null;
  return (
    <div
      style={{
        position: 'absolute',
        top: -4,
        right: -4,
        minWidth: 16,
        height: 16,
        padding: '1px 4px',
        borderRadius: 8,
        backgroundColor: BLUE,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        pointerEvents: 'none',
      }}
    >
      <span style={{ color: '#fff', fontSize: 9, fontWeight: 700, textAlign: 'center' }}>
        {count > 99 ? '99+' : count}
      </span>
    </div>
  );
}

// ── BottomNavBar ──────────────────────────────────────────────────────────

export const BottomNavBar = ({
  currentIndex,
  onTap,
  totalUnreadCount = 0,
  userAvatar = null,
}) => {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);

  useEffect(() => {
    const loadProfile = async () => {
      const cached = userProfileService.cachedProfile;
      if (cached) {
        setProfile(cached);
        return;
      }
      const profileData = await userProfileService.getCurrentUserProfile();
      if (profileData) setProfile(profileData);
    };
    loadProfile();
  }, []);

  const handleTabPress = (index) => {
    // TEMP DEBUG — remove once tabs are confirmed working.
    // If this never logs when you click, the click isn't reaching this
    // button at all (something else is covering it, or a second
    // BottomNavBar is mounted on top of this one).
    console.log('[BottomNavBar] tab pressed:', index);

    if (onTap) {
      onTap(index);
    } else {
      switch (index) {
        case 0:
          navigate('/chat');
          break;
        case 1:
          navigate('/');
          break;
        case 2:
          navigate('/settings');
          break;
        default:
          break;
      }
    }
  };

  const labelStyle = (isActive) => ({
    fontSize: 11,
    fontWeight: isActive ? 600 : 400,
    lineHeight: 1,
    marginTop: 4,
    color: isActive ? WHITE : GREY,
    pointerEvents: 'none',
  });

  return (
    <div
      style={{
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 50, // guarantee this sits above screen content
        pointerEvents: 'none', // wrapper itself is just a positioning shell
      }}
    >
      {/* Gradient background */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          pointerEvents: 'none',
          background: 'linear-gradient(to bottom, rgba(15,16,19,0) 0%, rgba(15,16,19,0.8) 100%)',
        }}
      />

      {/* Nav Bar */}
      <div style={{ position: 'relative', padding: '4px 12px 5px', pointerEvents: 'none' }}>
        <div
          style={{
            borderRadius: 30,
            padding: '4px 4px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-around',
            backgroundColor: 'rgba(35,38,44,0.30)',
            backdropFilter: 'blur(24px)',
            WebkitBackdropFilter: 'blur(24px)',
            border: '1px solid #23262C',
            boxShadow: '0 10px 30px rgba(0,0,0,0.25)',
            pointerEvents: 'auto', // the actual bar re-enables clicks
          }}
        >
          {/* Chats (LEFT) */}
          <button
            type="button"
            onClick={() => handleTabPress(0)}
            style={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: 0,
              zIndex: 1,
              position: 'relative',
            }}
          >
            <NavIcon isActive={currentIndex === 0}>
              <div style={{ position: 'relative' }}>
                <SvgIcon
                  src={currentIndex === 0 ? chatFilled : chatIcon}
                  size={26}
                  color={currentIndex === 0 ? WHITE : GREY}
                />
                <UnreadBadge count={totalUnreadCount} />
              </div>
            </NavIcon>
            <span style={labelStyle(currentIndex === 0)}>Chats</span>
          </button>

          {/* Home (MIDDLE) — larger icon */}
          <button
            type="button"
            onClick={() => handleTabPress(1)}
            style={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: 0,
              zIndex: 1,
              position: 'relative',
            }}
          >
            <NavIcon isActive={currentIndex === 1} isLarge>
              <SvgIcon
                src={currentIndex === 1 ? homeFilled : homeIcon}
                size={32}
                color={currentIndex === 1 ? WHITE : GREY}
              />
            </NavIcon>
            <span style={labelStyle(currentIndex === 1)}>FeelAtHome</span>
          </button>

          {/* Profile (RIGHT) */}
          <button
            type="button"
            onClick={() => handleTabPress(2)}
            style={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: 0,
              zIndex: 1,
              position: 'relative',
            }}
          >
            {/* FIX: previously branched on `profile` being loaded yet and
                showed a bespoke pulsing grey circle while it wasn't — a
                loading state Flutter's BottomNavBar simply doesn't have.
                Dart's _ProfileAvatar is rendered unconditionally, passing
                a possibly-null `profile` straight through to UserAvatar,
                which already knows how to fall back gracefully (generic
                person icon with no name yet, initials once the profile
                loads, real photo once that finishes loading too). Matching
                that here means always rendering ProfileAvatar and letting
                UserAvatar own every stage of the fallback, instead of a
                separate custom pulse animation unique to this port. */}
            <NavIcon isActive={currentIndex === 2} isAvatar>
              <ProfileAvatar isActive={currentIndex === 2} profile={profile} />
            </NavIcon>
            <span style={labelStyle(currentIndex === 2)}>You</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default BottomNavBar;